import React, { useState, useEffect, useCallback } from 'react';

// Types
interface AIMessage {
  type: 'ai' | 'user';
  text: string;
  isTyping?: boolean;
  isContinueButton?: boolean;
}

interface UserSelections {
  primaryChoice: string;
  relationship: string;
  careSituation: string;
  timeline: string;
  primaryConcern: string;
  askSolvynDetails: {
    personName: string;
    personAge: string;
    specificDetails: string;
    additionalInfo: string;
  };
}

interface Milestone {
  title: string;
  description: string;
  isExpanded: boolean;
  isHighlighted: boolean;
  tasks: string[];
}

interface Roadmap {
  title: string;
  duration: string;
  milestones: Milestone[];
  priorityTasks: string[];
}

// Scenario Data
const SCENARIO_DATA = {
  cancer: {
    careSituations: [
      'Recent health diagnosis or medical crisis',
      'Aging parent needing more support',
      'Chronic illness management',
      'Recovery from surgery or procedure',
      'Mental health or memory concerns',
      'End-of-life care planning'
    ],
    timelines: [
      'Just received the diagnosis (within days)',
      'Learning about treatment options (within weeks)',
      'Starting treatment soon',
      'Currently in treatment',
      'Planning for ongoing care'
    ],
    concerns: [
      'Understanding the diagnosis and next steps',
      'Coordinating family and telling others',
      'Managing work and daily responsibilities',
      'Preparing for treatment and care needs',
      'Dealing with the emotional impact'
    ],
    generateRoadmap: (selections: UserSelections): Roadmap => ({
      title: `Your 30-Day Family Coordination Plan: Supporting ${selections.askSolvynDetails.personName} Through Cancer Diagnosis`,
      duration: '30 days',
      milestones: [
        {
          title: `Process ${selections.askSolvynDetails.personName}'s diagnosis and gather information`,
          description: 'The first 72 hours after diagnosis are critical for making informed decisions. Having organized information prevents confusion and ensures nothing important is missed.',
          isExpanded: false,
          isHighlighted: false,
          tasks: [
            `Compile all of ${selections.askSolvynDetails.personName}'s medical documents and test results`,
            `Prepare questions for ${selections.askSolvynDetails.personName}'s next oncologist appointment`,
            `Research cancer treatment options for someone ${selections.askSolvynDetails.personName}'s age`,
            `Document ${selections.askSolvynDetails.personName}'s current symptoms and daily functioning`
          ]
        },
        {
          title: 'Coordinate immediate family communication',
          description: 'Early, clear communication prevents family conflicts and ensures everyone can provide appropriate support when it\'s needed most.',
          isExpanded: true,
          isHighlighted: true,
          tasks: [
            'Decide who in the family to tell first and when',
            'Prepare talking points for family conversations',
            'Schedule family meeting within 48-72 hours',
            'Create shared communication plan for ongoing updates'
          ]
        },
        {
          title: `Build ${selections.askSolvynDetails.personName}'s medical support team`,
          description: 'Having the right specialists and advocates in place early prevents delays in treatment and ensures coordinated care throughout the journey.',
          isExpanded: false,
          isHighlighted: false,
          tasks: [
            `Identify ${selections.askSolvynDetails.personName}'s primary oncologist and care coordinator`,
            'Research medical staff or interpreters if needed',
            'Understand insurance coverage and authorization requirements',
            'Establish patient advocate who can communicate effectively if needed'
          ]
        }
      ],
      priorityTasks: [
        'Schedule family meeting to discuss the diagnosis',
        `Gather questions for ${selections.askSolvynDetails.personName}'s next oncologist appointment`,
        'Research medical resources and support services'
      ]
    })
  },
  assistedLiving: {
    careSituations: [
      'Recent health diagnosis or medical crisis',
      'Aging parent needing more support',
      'Chronic illness management',
      'Recovery from surgery or procedure',
      'Mental health or memory concerns',
      'End-of-life care planning'
    ],
    timelines: [
      'Just starting to have concerns about safety',
      'Having initial conversations about options',
      'Actively researching assisted living facilities',
      'Planning the transition process',
      'Managing a recent change or crisis'
    ],
    concerns: [
      'Having productive conversations with my parent',
      'Getting family members aligned on next steps',
      'Understanding our housing and care options',
      'Managing the financial aspects',
      'Dealing with my parent\'s resistance to change'
    ],
    generateRoadmap: (selections: UserSelections): Roadmap => ({
      title: `Your 90-Day Housing Transition Plan: Finding ${selections.askSolvynDetails.personName} a Safe, Independent Living Solution`,
      duration: '90 days',
      milestones: [
        {
          title: `Have meaningful conversations with ${selections.askSolvynDetails.personName} about her future`,
          description: `Since ${selections.askSolvynDetails.personName} values independence and has deep roots in her current home, involving her in decision-making while addressing safety concerns is crucial for a successful transition.`,
          isExpanded: true,
          isHighlighted: true,
          tasks: [
            `Discuss ${selections.askSolvynDetails.personName}'s concerns about stairs and home maintenance`,
            'Explore what independence means in a new living situation',
            'Address attachment to home and memories',
            'Present assisted living as maintaining independence with practical support'
          ]
        },
        {
          title: `Evaluate ${selections.askSolvynDetails.personName}'s housing and care options`,
          description: 'Understanding options that address mobility limitations while preserving autonomy helps you present solutions rather than just problems.',
          isExpanded: false,
          isHighlighted: false,
          tasks: [
            'Research assisted living communities with accessible, single-level living',
            'Look for communities with gardening programs or outdoor spaces',
            'Evaluate costs and what current home sale could cover',
            'Consider home modification alternatives if staying is preferred'
          ]
        }
      ],
      priorityTasks: [
        `Have conversation with ${selections.askSolvynDetails.personName} about daily challenges with stairs and housework`,
        'Research assisted living communities with garden programs',
        'Schedule first facility tour focused on accessibility and outdoor spaces'
      ]
    })
  }
};

function App() {
  // Screen Management
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [currentScenario, setCurrentScenario] = useState<'cancer' | 'assistedLiving' | null>(null);
  
  // User Selections
  const [userSelections, setUserSelections] = useState<UserSelections>({
    primaryChoice: '',
    relationship: '',
    careSituation: '',
    timeline: '',
    primaryConcern: '',
    askSolvynDetails: {
      personName: '',
      personAge: '',
      specificDetails: '',
      additionalInfo: ''
    }
  });
  
  // Progress Tracking
  const [progressStep, setProgressStep] = useState(0);
  const [showAskSolvyn, setShowAskSolvyn] = useState(false);
  
  // AI Chat
  const [aiMessages, setAiMessages] = useState<AIMessage[]>([]);
  
  // Generated Roadmap
  const [generatedRoadmap, setGeneratedRoadmap] = useState<Roadmap | null>(null);
  
  // Ask Solvyn Chat State
  const [askSolvynStep, setAskSolvynStep] = useState(0); // 0-8 steps as described
  const [askSolvynMessages, setAskSolvynMessages] = useState<{type: 'ai' | 'user', text: string}[]>([]);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [currentAiMessage, setCurrentAiMessage] = useState('');
  const [showUserInput, setShowUserInput] = useState(false);
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);
  
  // Roadmap State
  const [expandedMilestones, setExpandedMilestones] = useState<number[]>([]); // All milestones collapsed by default
  
  // Save Roadmap State
  const [emailInput, setEmailInput] = useState('');

  // Auto-scroll to top when entering save roadmap screen
  useEffect(() => {
    if (currentScreen === 'save-roadmap') {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        // Target the specific save roadmap scrollable container
        const saveRoadmapContainer = document.querySelector('[data-save-roadmap-container]') as HTMLElement;
        if (saveRoadmapContainer) {
          saveRoadmapContainer.scrollTop = 0;
          console.log('Save roadmap container scrolled to top:', saveRoadmapContainer.scrollTop);
        } else {
          console.log('Save roadmap container not found');
        }
        
        // Also ensure window and document are at top as fallback
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    }
  }, [currentScreen]);

  // Screen Navigation
  const screens = [
    'welcome',
    'setup-promise',
    'primary-choice',
    'relationship',
    'care-situation',
    'timeline',
    'primary-concern',
    'ask-solvyn-details',
    'roadmap',
    'save-roadmap',
    'commitment'
  ];

  const nextScreen = () => {
    const currentIndex = screens.indexOf(currentScreen);
    if (currentIndex < screens.length - 1) {
      setCurrentScreen(screens[currentIndex + 1]);
      setProgressStep(currentIndex + 1);
    }
  };

  const prevScreen = () => {
    const currentIndex = screens.indexOf(currentScreen);
    if (currentIndex > 0) {
      setCurrentScreen(screens[currentIndex - 1]);
      setProgressStep(currentIndex - 1);
    }
  };

  // Selection Handlers
  const handleSelection = (key: keyof UserSelections, value: string) => {
    setUserSelections(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Roadmap Functions
  const toggleMilestone = (index: number) => {
    setExpandedMilestones(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Ask Solvyn Chat Functions
  const simulateAiTyping = useCallback((message: string, callback: () => void) => {
    setIsAiTyping(true);
    setCurrentAiMessage('');
    let index = 0;
    
    const typeInterval = setInterval(() => {
      if (index < message.length) {
        setCurrentAiMessage(message.substring(0, index + 1));
        index++;
        
        // Auto-scroll during AI typing
        const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      } else {
        clearInterval(typeInterval);
      setIsAiTyping(false);
      callback();
      }
    }, 80); // Slower, more realistic typing speed
  }, []);

  function simulateUserTyping() {
    if (askSolvynStep === 1) {
      // Step 3: Simulate first user message
      const message = "My mom Helen was just diagnosed with breast cancer";
      let index = 0;
      setIsUserTyping(true);
      setCurrentUserMessage('');
      
      const typeInterval = setInterval(() => {
        if (index < message.length) {
          setCurrentUserMessage(message.substring(0, index + 1));
          index++;
          
          // Auto-scroll during typing
          const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        } else {
          clearInterval(typeInterval);
          setIsUserTyping(false);
          // After typing completes, show send button
          setAskSolvynStep(2);
        }
      }, 100); // Slower, more realistic typing speed
    } else if (askSolvynStep === 5) {
      // Step 7: Simulate second user message
      const message = "She's 75, lives alone, and speaks better Portuguese than English";
      let index = 0;
      setIsUserTyping(true);
      setCurrentUserMessage('');
      
      const typeInterval = setInterval(() => {
        if (index < message.length) {
          setCurrentUserMessage(message.substring(0, index + 1));
          index++;
          
          // Auto-scroll during typing
          const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        } else {
          clearInterval(typeInterval);
          setIsUserTyping(false);
          // After typing completes, show send button
          setAskSolvynStep(6);
        }
      }, 100); // Slower, more realistic typing speed
    }
  }

  function sendUserMessage() {
    if (askSolvynStep === 2) {
      // Step 3: Send first user message
      setAskSolvynMessages(prev => [...prev, { type: 'user', text: currentUserMessage }]);
      setCurrentUserMessage('');
      setShowUserInput(false);
      setAskSolvynStep(3);
      
      // Auto-scroll after message is sent
      setTimeout(() => {
        const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 100);
      
      // Step 4: Show AI response with typing simulation
      setTimeout(() => {
        simulateAiTyping("Thank you for sharing that. I'm creating clear, step-by-step guidance for supporting Helen through her breast cancer diagnosis - removing all the uncertainty about what comes next and how to coordinate your family.", () => {
          setAskSolvynMessages(prev => [...prev, {
            type: 'ai',
            text: "Thank you for sharing that. I'm creating clear, step-by-step guidance for supporting Helen through her breast cancer diagnosis - removing all the uncertainty about what comes next and how to coordinate your family."
          }]);
          setAskSolvynStep(4);
          
          // Auto-scroll after AI message
          setTimeout(() => {
            const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
            if (chatContainer) {
              chatContainer.scrollTop = chatContainer.scrollHeight;
            }
          }, 100);
          
          // Step 5: Show second AI question with typing simulation
          setTimeout(() => {
            simulateAiTyping("Is there anything else about Helen's situation I should know? For example, her age, living situation, or any other important details?", () => {
              setAskSolvynMessages(prev => [...prev, {
          type: 'ai',
                text: "Is there anything else about Helen's situation I should know? For example, her age, living situation, or any other important details?"
              }]);
              setAskSolvynStep(5);
              setShowUserInput(true);
              
              // Auto-scroll after second AI message
              setTimeout(() => {
                const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
                if (chatContainer) {
                  chatContainer.scrollTop = chatContainer.scrollHeight;
                }
              }, 100);
            });
          }, 1000);
        });
      }, 1000);
    } else if (askSolvynStep === 6) {
      // Step 7: Send second user message
      setAskSolvynMessages(prev => [...prev, { type: 'user', text: currentUserMessage }]);
      setCurrentUserMessage('');
      setShowUserInput(false);
      setAskSolvynStep(7);
      
      // Auto-scroll after message is sent
      setTimeout(() => {
        const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 100);
      
      // Step 8: Show final AI response with typing simulation
      setTimeout(() => {
        simulateAiTyping("Perfect. I'm weaving Helen's age, living situation, and language preferences into your personalized guidance. This ensures every step is tailored to what will actually work for Helen's specific circumstances. Click below to create your guide.", () => {
          setAskSolvynMessages(prev => [...prev, {
            type: 'ai',
            text: "Perfect. I'm weaving Helen's age, living situation, and language preferences into your personalized guidance. This ensures every step is tailored to what will actually work for Helen's specific circumstances. Click below to create your guide."
          }]);
          setAskSolvynStep(8); // Show "Generate Roadmap" button
          
          // Auto-scroll after final AI message
          setTimeout(() => {
            const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
            if (chatContainer) {
              chatContainer.scrollTop = chatContainer.scrollHeight;
            }
          }, 100);
        });
      }, 1000);
    }
  }

  function generateRoadmap() {
    setIsGeneratingRoadmap(true);
    setAskSolvynStep(9); // Show spinner state
    
    // Simulate 3 seconds of processing
    setTimeout(() => {
      setIsGeneratingRoadmap(false);
      
      // Show final message with typing simulation
      simulateAiTyping("Your guide for Helen's journey is here. Click below to see your personalized steps.", () => {
        setAskSolvynMessages(prev => [...prev, {
          type: 'ai',
          text: "Your guide for Helen's journey is here. Click below to see your personalized steps."
        }]);
        setAskSolvynStep(10); // Show "Continue" button
        
        // Auto-scroll after final message
        setTimeout(() => {
          const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
      });
    }, 3000);
  }

  function continueToRoadmap() {
    nextScreen();
  }

  // Screen Renderers
  const renderProgressBar = () => {
    const totalSteps = 7; // Only count the main onboarding steps
    const currentStepIndex = Math.min(progressStep, totalSteps - 1);
    const progressPercentage = (currentStepIndex / (totalSteps - 1)) * 100;
    
    return (
      <div style={{
        padding: '60px 24px 20px 24px',
        background: '#FFFFFF'
      }}>
        <div style={{
          height: '12px',
          background: '#F0EEEE',
          borderRadius: '6px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #4A90A4 0%, #357A8A 100%)',
            width: `${progressPercentage}%`,
            borderRadius: '6px',
            transition: 'width 500ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)'
          }} />
        </div>
      </div>
    );
  };

  const renderWelcome = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#FDFCFC',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '40px 20px',
    }}>
      <div style={{
        marginBottom: '24px'
      }}>
        <div style={{
          fontSize: '64px',
          fontWeight: '700',
          color: '#4A90A4',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          letterSpacing: '-0.02em',
          textAlign: 'center'
        }}>
          Solvyn
        </div>
      </div>
      <h1 style={{
        fontSize: '28px',
        fontWeight: '600',
        color: '#1A1A1A',
        marginBottom: '12px',
        lineHeight: '36px'
      }}>
        Turn overwhelming decisions into a plan you can follow
      </h1>
      <button
        onClick={nextScreen}
        style={{
          background: '#4A90A4',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '12px',
          padding: '16px 24px',
          fontSize: '18px',
          fontWeight: '600',
          minHeight: '48px',
          cursor: 'pointer',
          transition: 'all 200ms ease'
        }}
      >
        CONTINUE
      </button>
    </div>
  );

  const renderSetupPromise = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#FDFCFC',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '40px 20px'
    }}>
      <div style={{
        marginBottom: '24px'
      }}>
        <div style={{
          fontSize: '64px',
          fontWeight: '700',
          color: '#4A90A4',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          letterSpacing: '-0.02em',
          textAlign: 'center'
        }}>
          Solvyn
        </div>
      </div>
      <h1 style={{
        fontSize: '28px',
        fontWeight: '600',
        color: '#1A1A1A',
        marginBottom: '12px',
        lineHeight: '36px'
      }}>
        5 questions to chart your course for what's next
      </h1>
      <button
        onClick={nextScreen}
        style={{
          background: '#4A90A4',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '12px',
          padding: '16px 24px',
          fontSize: '18px',
          fontWeight: '600',
          minHeight: '48px',
          cursor: 'pointer',
          transition: 'all 200ms ease'
        }}
      >
        CONTINUE
      </button>
    </div>
  );

  const renderOptionSelection = (title: string, options: string[], selectionKey: keyof UserSelections) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#FDFCFC'
    }}>
      {renderProgressBar()}
      <div style={{
        flex: 1,
        padding: '24px',
        paddingBottom: '100px', // Space for fixed bottom button
        overflowY: 'auto'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: '600',
            color: '#1A1A1A',
            marginBottom: '12px',
            lineHeight: '40px',
            letterSpacing: '-0.02em'
          }}>
            {title}
          </h2>
        </div>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {options.map((option, index) => {
            const isSelected = userSelections[selectionKey] === option;
  return (
              <button
                key={index}
                onClick={() => handleSelection(selectionKey, option)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  background: isSelected 
                    ? 'linear-gradient(135deg, #4A90A4 0%, #357A8A 100%)' 
                    : '#FFFFFF',
                  color: isSelected ? '#FFFFFF' : '#1A1A1A',
                  border: `2px solid ${isSelected ? 'transparent' : '#F0EEEE'}`,
                  borderRadius: '16px',
                  fontSize: '18px',
                  fontWeight: '500',
                  minHeight: '56px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isSelected 
                    ? '0 8px 24px rgba(74, 144, 164, 0.3)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = '#4A90A4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = '#F0EEEE';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>{option}</span>
                  {isSelected && (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Supportive message after selection */}
        {userSelections[selectionKey] && selectionKey !== 'relationship' && selectionKey !== 'timeline' && (
          <div style={{
            marginTop: '24px',
            padding: '16px 20px',
            background: '#F8F6F6',
            borderRadius: '12px',
            border: '1px solid #F0EEEE'
          }}>
            <div style={{
              fontSize: '16px',
              color: '#4A90A4',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {selectionKey === 'careSituation' && "We're here to help you navigate this"}
              {selectionKey === 'primaryConcern' && "We'll focus on what matters most right now"}
            </div>
          </div>
        )}
      </div>
      
      {/* Fixed bottom Continue button - Duolingo style */}
      {userSelections[selectionKey] && (
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          padding: '20px 24px',
          background: '#FFFFFF',
          borderTop: '1px solid #F0EEEE',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
        }}>
            <button
              onClick={nextScreen}
              style={{
                width: '100%',
                background: '#4A90A4',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 24px',
                fontSize: '18px',
                fontWeight: '600',
                minHeight: '48px',
                cursor: 'pointer',
                transition: 'all 200ms ease'
              }}
            >
              Continue
            </button>
          </div>
        )}
      
      {showAskSolvyn && (
        <button
          onClick={() => {/* Chat interface would open here */}}
          style={{
            position: 'absolute',
            bottom: userSelections[selectionKey] ? '100px' : '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4A90A4 0%, #357A8A 100%)',
            color: '#FFFFFF',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(74, 144, 164, 0.4)',
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(74, 144, 164, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(74, 144, 164, 0.4)';
          }}
        >
          💬
        </button>
      )}
    </div>
  );

  const renderAskSolvynDetails = () => {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
        {/* Background Progress Bar */}
        {renderProgressBar()}
        
        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          
          {/* Chat Interface */}
    <div style={{
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            background: 'rgba(0,0,0,0.5)', 
      display: 'flex',
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '20px'
    }}>
      <div style={{
              background: 'white', 
          borderRadius: '16px',
              padding: '24px', 
              width: '100%', 
              maxWidth: '500px',
              maxHeight: '80vh',
        display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Chat Header */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>Ask Solvyn</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Your Navigation Guide</div>
      </div>
      
              {/* Chat Messages */}
              <div data-chat-container style={{ marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}>
                {askSolvynMessages.map((message, index) => (
            <div key={index} style={{
                    marginBottom: '12px',
              display: 'flex',
              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
            }}>
              <div style={{
                      maxWidth: '80%',
                padding: '12px 16px',
                      borderRadius: '12px',
                      background: message.type === 'user' ? '#4A90A4' : '#F0F0F0',
                      color: message.type === 'user' ? 'white' : '#1a1a1a',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      textAlign: 'left',
                      direction: 'ltr'
              }}>
                {message.text}
              </div>
            </div>
          ))}
          
                {/* Current User Typing */}
                {isUserTyping && (
                  <div style={{ 
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}>
                    <div style={{ 
                      maxWidth: '80%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: '#4A90A4',
                      color: 'white',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      textAlign: 'left',
                      direction: 'ltr',
                      minHeight: '60px',
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      {currentUserMessage}
                      <span style={{ opacity: 0.7 }}>|</span>
                    </div>
                  </div>
                )}

                {/* Current AI Typing */}
          {isAiTyping && (
            <div style={{
                    marginBottom: '12px',
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <div style={{
                      maxWidth: '80%',
                padding: '12px 16px',
                      borderRadius: '12px',
                      background: '#F0F0F0',
                      color: '#1a1a1a',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      textAlign: 'left',
                      direction: 'ltr',
                      minHeight: '60px',
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      {currentAiMessage}
                      <span style={{ opacity: 0.7 }}>|</span>
              </div>
            </div>
          )}
        </div>
        
              {/* User Input Area - Step 2 & 6 */}
              {showUserInput && (askSolvynStep === 1 || askSolvynStep === 5) && !isUserTyping && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{
          display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '12px'
                  }}>
                    <div style={{ 
                      maxWidth: '80%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      background: '#4A90A4',
                      color: 'white',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      minHeight: '60px',
                      minWidth: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <span style={{ opacity: 0.7, fontSize: '16px' }}>|</span>
                    </div>
                  </div>
                  <button 
                    onClick={simulateUserTyping}
                    disabled={isUserTyping}
            style={{
                      width: '100%',
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      cursor: isUserTyping ? 'not-allowed' : 'pointer', 
                      background: isUserTyping ? '#E0E0E0' : 'transparent', 
                      color: isUserTyping ? '#999' : '#4A90A4', 
                      border: '1px solid #4A90A4' 
                    }}
                  >
                    {isUserTyping ? 'Typing...' : 'Simulate User Typing'}
                  </button>
                </div>
              )}

              {/* Send Button - Step 3 & 7 */}
              {(askSolvynStep === 2 || askSolvynStep === 6) && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ 
              flex: 1,
              padding: '12px 16px',
              borderRadius: '12px',
                    background: '#4A90A4',
                    color: 'white',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {currentUserMessage}
                  </div>
          <button
                    onClick={sendUserMessage}
            style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#4A90A4',
                      color: 'white',
              border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px'
                    }}
                  >
                    ↑
          </button>
        </div>
              )}
        
              {/* Generate Roadmap Button - Step 8 */}
              {askSolvynStep === 8 && (
                <div style={{ display: 'flex', gap: '12px' }}>
          <button
                    onClick={generateRoadmap}
            style={{
                      flex: 1, 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      cursor: 'pointer',
              background: '#4A90A4',
                      color: 'white', 
                      border: 'none' 
                    }}
                  >
                    Create Helen's journey forward
                  </button>
                </div>
              )}

              {/* Spinner - Step 9 */}
              {askSolvynStep === 9 && isGeneratingRoadmap && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #4A90A4',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ marginLeft: '12px', color: '#666', fontSize: '14px' }}>Creating your guide...</span>
                </div>
              )}

              {/* Continue Button - Step 10 */}
              {askSolvynStep === 10 && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={continueToRoadmap}
                    style={{
                      flex: 1, 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      fontSize: '14px', 
                      fontWeight: '500', 
              cursor: 'pointer',
                      background: '#4A90A4', 
                      color: 'white', 
                      border: 'none' 
            }}
          >
                    Continue
          </button>
                </div>
        )}
            </div>
          </div>
      </div>
    </div>
  );
  };

  const renderRoadmap = () => {
    const milestones = [
      {
        title: "Process Helen's diagnosis and gather information",
        why: "The first 72 hours after diagnosis are critical for making informed decisions. Having organized information prevents confusion and ensures nothing important is missed.",
        tasks: [
          "Compile all of Helen's medical documents and test results",
          "Prepare questions for Helen's next oncologist appointment",
          "Research breast cancer treatment options for someone Helen's age",
          "Document Helen's current symptoms and daily functioning"
        ],
        isExpanded: expandedMilestones.includes(0),
        isPriority: true
      },
      {
        title: "Coordinate immediate family communication",
        why: "Early, clear communication prevents family conflicts and ensures everyone can provide appropriate support when it's needed most.",
        tasks: [
          "Decide who in the family to tell first and when",
          "Prepare talking points for family conversations (consider Portuguese translation)",
          "Schedule family meeting within 48-72 hours",
          "Create shared communication plan for ongoing updates about Helen"
        ],
        isExpanded: expandedMilestones.includes(1),
        isPriority: false
      },
      {
        title: "Build Helen's medical support team",
        why: "Having the right specialists and advocates in place early prevents delays in treatment and ensures coordinated care throughout the journey.",
        tasks: [
          "Identify Helen's primary oncologist and care coordinator",
          "Research Portuguese-speaking medical staff or interpreters",
          "Understand Helen's insurance coverage and authorization requirements",
          "Establish patient advocate who can communicate in Portuguese if needed"
        ],
        isExpanded: expandedMilestones.includes(2),
        isPriority: false
      },
      {
        title: "Prepare Helen's living situation for treatment",
        why: "Since Helen lives alone, anticipating her care needs during treatment is crucial for her safety and peace of mind.",
        tasks: [
          "Assess Helen's home for treatment compatibility and safety",
          "Identify transportation options for Helen's frequent appointments",
          "Plan for treatment side effects and who will check on Helen",
          "Arrange backup care and emergency contacts for Helen's home"
        ],
        isExpanded: expandedMilestones.includes(3),
        isPriority: false
      },
      {
        title: "Manage work and family coordination",
        why: "Balancing caregiving with other responsibilities requires proactive planning to prevent burnout and maintain family stability.",
        tasks: [
          "Discuss Helen's care responsibilities with your employer",
          "Coordinate care shifts with other family members",
          "Identify household tasks Helen may need help with",
          "Set up meal planning and delivery systems for Helen"
        ],
        isExpanded: expandedMilestones.includes(4),
        isPriority: false
      }
    ];

    const priorityTasks = [
      "Schedule family meeting to discuss Helen's diagnosis",
      "Gather questions for Helen's next oncologist appointment",
      "Research Portuguese-speaking medical resources"
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fdfcfc' }}>
        
        {/* Main Content */}
        <div style={{ flex: 1, padding: '20px', paddingTop: '60px', paddingBottom: '120px', overflowY: 'auto' }}>
          {/* Journey Overview Card */}
          <div style={{ background: '#8B5A73', color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>Your Journey</h2>
            <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '16px' }}>Complete roadmap to supporting Helen through her breast cancer diagnosis</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>0</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Completed</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>0</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>In Progress</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>5</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Not Started</div>
              </div>
            </div>
            </div>
            
          {/* Journey Steps Timeline */}
          <div style={{ position: 'relative' }}>
            {/* Timeline Line */}
            <div style={{ position: 'absolute', left: '28px', top: '0', bottom: '0', width: '2px', background: '#F0EEEE', zIndex: 1 }}></div>
            
            {/* Milestones */}
            {milestones.map((milestone, index) => (
              <div key={index} style={{ position: 'relative', marginBottom: '24px', zIndex: 2 }}>
                <div 
                  style={{ 
                    background: 'white', 
                    padding: '16px', 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: milestone.isPriority ? '2px solid #8B5A73' : '1px solid #E0E0E0'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>{milestone.title}</h3>
            <div style={{
                      background: '#F8F6F6', 
                      color: '#999', 
                      padding: '4px 8px', 
                  borderRadius: '12px',
                      fontSize: '12px', 
                      fontWeight: '500' 
                    }}>
                      Not Started
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>{milestone.why}</div>
                  
                  {milestone.isExpanded && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {milestone.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} style={{
                          background: '#F8F6F6',
                            borderRadius: '8px', 
                            padding: '12px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px' 
                          }}>
                            <div style={{ 
                              width: '16px', 
                              height: '16px', 
                              borderRadius: '50%', 
                              background: '#F0EEEE', 
                              color: '#999', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              fontSize: '10px' 
                            }}>○</div>
                            <span style={{ fontSize: '14px', color: '#666' }}>{task}</span>
                        </div>
                      ))}
                      </div>
                    </div>
                  )}
                  
                  <div 
                    style={{ 
                      fontSize: '12px', 
                      color: '#8B5A73', 
                      textAlign: 'center', 
                      padding: '8px', 
                      background: 'rgba(139, 90, 115, 0.1)', 
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: '1px solid rgba(139, 90, 115, 0.2)'
                    }}
                    onClick={() => toggleMilestone(index)}
                  >
                    {milestone.isExpanded ? 'Hide tasks ↑' : `View all tasks (${milestone.tasks.length})`}
                  </div>
                </div>
                </div>
              ))}
            </div>
            
          {/* Next 48 Hours Priority Tasks */}
          <div style={{ marginTop: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '16px' }}>Next 48 Hours Priority Tasks</h3>
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              {priorityTasks.map((task, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#8B5A73' }}></div>
                  <div style={{ fontSize: '14px', color: '#1a1a1a' }}>{task}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Your Roadmap Button */}
          <div style={{ marginTop: '32px', textAlign: 'center' }}>
            <button
              onClick={() => {
                nextScreen();
              }}
              style={{
              background: '#8B5A73',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 32px',
                  fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(139, 90, 115, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#7A4A63';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 90, 115, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#8B5A73';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 90, 115, 0.3)';
              }}
            >
              Save Helen's Roadmap
            </button>
                </div>
            </div>
      </div>
    );
  };

  const renderSaveRoadmap = () => {
    const handleGoogleSSO = () => {
      // TODO: Implement Google SSO
      alert('Google SSO integration coming soon!');
    };

    const handleEmailSubmit = () => {
      if (emailInput.trim()) {
        // TODO: Implement email link sending
        alert(`Link will be sent to: ${emailInput}`);
      } else {
        alert('Please enter your email or phone number');
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fdfcfc', paddingTop: '60px' }}>
        <div data-save-roadmap-container style={{ flex: 1, padding: '20px', paddingBottom: '120px', overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px', lineHeight: '32px' }}>
              Let's save Helen's roadmap
            </h1>
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '24px', maxWidth: '300px', margin: '0 auto' }}>
              Take control of Helen's transition with a plan you can actually follow
            </p>
          </div>

          {/* Key Statements */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '40px' }}>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', lineHeight: '24px' }}>
                Stop second-guessing every decision about Helen's care
              </div>
            </div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', lineHeight: '24px' }}>
                Get your family aligned instead of fighting about next steps
              </div>
            </div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', lineHeight: '24px' }}>
                Know exactly what to do next instead of feeling paralyzed
              </div>
            </div>
          </div>

          {/* Primary Action Zone */}
          <div style={{ marginBottom: '32px' }}>
            <button
              onClick={handleGoogleSSO}
              style={{
                width: '100%',
                background: '#4285F4',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(66, 133, 244, 0.3)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#3367D6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#4285F4';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '20px' }}>G</span>
              Continue with Google
            </button>
          </div>

          {/* Divider */}
          <div style={{ position: 'relative', textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ height: '1px', background: '#E0E0E0', width: '100%' }}></div>
      <div style={{
              position: 'absolute', 
              top: '-10px', 
              left: '50%', 
              transform: 'translateX(-50%)', 
              background: '#fdfcfc', 
              padding: '0 16px', 
              fontSize: '14px', 
              color: '#999' 
            }}>
              OR
            </div>
          </div>

          {/* Alternative Option */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Enter your email or phone number"
              style={{
                width: '100%',
                padding: '16px 20px',
                fontSize: '16px',
                border: '2px solid #E0E0E0',
                borderRadius: '12px',
                background: 'white',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#8B5A73';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E0E0E0';
              }}
            />
        <button
              onClick={handleEmailSubmit}
          style={{
            width: '100%',
                background: '#8B5A73',
                color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '16px 24px',
                fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(139, 90, 115, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#7A4A63';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#8B5A73';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Send me the link
        </button>
            <p style={{ 
              fontSize: '12px', 
              color: '#999', 
              textAlign: 'center', 
              lineHeight: '18px',
              margin: '8px 0 0 0'
            }}>
              We'll send you a secure link to access Helen's roadmap
            </p>
          </div>
      </div>
    </div>
  );
  };

  const renderCommitment = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#FDFCFC',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '40px 20px'
    }}>
      <div style={{
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, #4A90A4, #357A8A)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '40px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(74, 144, 164, 0.3)'
      }}>
        🎉
      </div>
      <h1 style={{
        fontSize: '28px',
        fontWeight: '600',
        color: '#1A1A1A',
        marginBottom: '12px',
        lineHeight: '36px'
      }}>
        Save {userSelections.askSolvynDetails.personName || 'your'} roadmap and get ongoing support
      </h1>
      <p style={{
        fontSize: '18px',
        color: '#666',
        marginBottom: '32px',
        lineHeight: '24px'
      }}>
        We'll check in with you as the situation evolves
      </p>
      <input
        type="email"
        placeholder="Enter your email address"
        style={{
          width: '100%',
          maxWidth: '300px',
          padding: '16px',
          border: '2px solid #F0EEEE',
          borderRadius: '12px',
          fontSize: '18px',
          marginBottom: '16px',
          minHeight: '48px'
        }}
      />
      <button
        onClick={() => setCurrentScreen('welcome')}
        style={{
          background: '#4A90A4',
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '12px',
          padding: '16px 24px',
          fontSize: '18px',
          fontWeight: '600',
          minHeight: '48px',
          cursor: 'pointer',
          transition: 'all 200ms ease'
        }}
      >
        Create Account & Save
      </button>
    </div>
  );

  // Main Render
  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case 'welcome':
        return renderWelcome();
      case 'setup-promise':
        return renderSetupPromise();
      case 'primary-choice':
        return renderOptionSelection(
          'What would you like guidance with?',
          ['Caring for Others', 'Health & Wellness', 'Career & Growth', 'Life Transitions', 'Financial Planning', 'Personal Goals'],
          'primaryChoice'
        );
      case 'relationship':
        return renderOptionSelection(
          'Who are you caring for?',
          ['My parent or family member', 'My spouse or partner', 'My child', 'A close friend', 'Multiple family members'],
          'relationship'
        );
      case 'care-situation':
        return renderOptionSelection(
          'What caregiving situation are you navigating?',
          currentScenario ? SCENARIO_DATA[currentScenario].careSituations : [
            'Recent health diagnosis or medical crisis',
            'Aging parent needing more support',
            'Chronic illness management',
            'Recovery from surgery or procedure',
            'Mental health or memory concerns',
            'End-of-life care planning'
          ],
          'careSituation'
        );
      case 'timeline':
        return renderOptionSelection(
          'Where are you in this journey?',
          currentScenario ? SCENARIO_DATA[currentScenario].timelines : [
            'Just received the diagnosis (within days)',
            'Learning about treatment options (within weeks)',
            'Starting treatment soon',
            'Currently in treatment',
            'Planning for ongoing care'
          ],
          'timeline'
        );
      case 'primary-concern':
        return renderOptionSelection(
          'What feels most overwhelming right now?',
          currentScenario ? SCENARIO_DATA[currentScenario].concerns : [
            'Understanding the diagnosis and next steps',
            'Coordinating family and telling others',
            'Managing work and daily responsibilities',
            'Preparing for treatment and care needs',
            'Dealing with the emotional impact',
            'Finding the right resources and support',
            'Balancing caregiving with other responsibilities',
            'Making difficult decisions about care',
            'Communicating with healthcare providers',
            'Managing financial concerns'
          ],
          'primaryConcern'
        );
      case 'ask-solvyn-details':
        return renderAskSolvynDetails();
      case 'roadmap':
        return renderRoadmap();
      case 'save-roadmap':
        return renderSaveRoadmap();
      case 'commitment':
        return renderCommitment();
      default:
        return renderWelcome();
    }
  };

  // Determine scenario based on selections
  useEffect(() => {
    if (userSelections.careSituation === 'Recent health diagnosis or medical crisis') {
      setCurrentScenario('cancer');
    } else if (userSelections.careSituation === 'Aging parent needing more support') {
      setCurrentScenario('assistedLiving');
    }
  }, [userSelections.careSituation]);

  // Show Ask Solvyn after primary choice
  useEffect(() => {
    if (userSelections.primaryChoice) {
      setShowAskSolvyn(true);
    }
  }, [userSelections.primaryChoice]);

  // Generate roadmap when reaching that screen
  useEffect(() => {
    if (currentScreen === 'roadmap' && currentScenario && !generatedRoadmap) {
      const roadmap = SCENARIO_DATA[currentScenario].generateRoadmap(userSelections);
      setGeneratedRoadmap(roadmap);
    }
  }, [currentScreen, currentScenario, generatedRoadmap, userSelections]);


  // Reset chat state when entering ask-solvyn-details screen
  useEffect(() => {
    if (currentScreen === 'ask-solvyn-details') {
      setAskSolvynStep(0);
      setAskSolvynMessages([]);
      setIsUserTyping(false);
      setIsAiTyping(false);
      setCurrentUserMessage('');
      setCurrentAiMessage('');
      setShowUserInput(false);
      setIsGeneratingRoadmap(false);
    }
  }, [currentScreen]);

  // Start conversation when entering ask-solvyn-details screen
  useEffect(() => {
    if (currentScreen === 'ask-solvyn-details' && askSolvynStep === 0) {
      setTimeout(() => {
        // Step 1: Show AI initial message with typing animation
        const initialMessage = "To create guidance that fits your exact situation, can you share a few more details? The more you're comfortable telling me, the better I can help. For example, what's the person's name, your relationship, and what type of cancer were they diagnosed with?";
        
        simulateAiTyping(initialMessage, () => {
          setAskSolvynMessages([{
            type: 'ai',
            text: initialMessage
          }]);
          setAskSolvynStep(1);
          setShowUserInput(true);
        });
      }, 500);
    }
  }, [currentScreen, askSolvynStep]);

  // Auto-scroll to bottom of chat when messages change
  useEffect(() => {
    const chatContainer = document.querySelector('[data-chat-container]') as HTMLElement;
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [askSolvynMessages, isUserTyping, isAiTyping]);

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: '#FDFCFC',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {renderCurrentScreen()}
    </div>
  );
}

export default App;